import { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../store';
import type { FSxpertButtonProps } from '@tlveng/fsxpert-sdk';

/** Full FSxpert button config (includes contextKey) */
export type FSxpertConfig = FSxpertButtonProps['config'];

/** Base config without contextKey/payload - for building configs */
export type FSxpertBaseConfig = Omit<FSxpertConfig, 'contextKey' | 'payload'>;

/**
 * Utility to convert a label to kebab-case contextKey
 * Format: {prefix}--{kebab-case-label}
 */
export const toContextKey = (prefix: string, label: string): string => {
    const kebabLabel = label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    return `${prefix}--${kebabLabel}`;
};


export const useFSxpertBaseConfig = (): FSxpertBaseConfig => {
    const { idTokenPayload: userMetadata, accessToken } = useSelector(
        (state: RootState) => state.auth
    );
    const { selectedAccountId: accountId, isDemo: isDemoMode } = useSelector(
        (state: RootState) => state.tenancy
    );
    const features = useSelector(
        (state: RootState) => ({
            byKey: state.features.byKey,
            active: state.features.active,
        }),
        shallowEqual
    );

    const domain = import.meta.env.VITE_IS_APP_INTEGRATED_MFE
        ? import.meta.env.VITE_WLM_FRONTEND_URL
        : location.origin;

    return useMemo<FSxpertBaseConfig>(
        () => ({
            userMetadata,
            isDemoMode,
            accessToken: accessToken ?? '',
            accountId: accountId ?? '',
            features,
            extraData: { domain },
        }),
        [userMetadata, isDemoMode, accessToken, accountId, features, domain]
    );
};
